import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import { selectPlan } from '../../../utils/signupApi';

const DISPLAY_NAME_HELPER_TEXT =
  'The display name (for example artist\u2019s name). This display name will auto populate each catalog entry as the creator.';

interface SignupStep4AccountDetailsProps {
  onNext: () => void;
  onBack: () => void;
  onPaymentRedirect?: (stripeUrl: string) => void;
}

const BUSINESS_TYPES = [
  'Gallery',
  'Estate',
  'Museum',
  'Auction House',
  'Foundation',
  'University',
  'Corporate Collection',
  'Other',
] as const;

export const SignupStep4AccountDetails: React.FC<SignupStep4AccountDetailsProps> = ({
  onNext,
  onBack: _onBack,
  onPaymentRedirect,
}) => {
  const { state, setContactInfo, setError, setLoading } = useSignupWizard();

  const [localError, setLocalError] = React.useState<string | null>(null);
  const [localInitials, setLocalInitials] = React.useState(state.initials || '');
  const [initialsManuallyEdited, setInitialsManuallyEdited] = React.useState(
    Boolean(state.initials)
  );
  const [localBusinessName, setLocalBusinessName] = React.useState(state.businessName || '');
  const [localBusinessType, setLocalBusinessType] = React.useState(state.businessType || '');

  const isPaidPlan = state.selectedTier === 'creator' || state.selectedTier === 'commercial';
  const isCommercialPlan = state.selectedTier === 'commercial';
  const isCreatorPlan = state.selectedTier === 'creator';
  const isDelegatePlan = state.selectedTier === 'delegate';
  const needsInitials = isPaidPlan || isDelegatePlan;

  const [localDisplayName, setLocalDisplayName] = React.useState(state.displayName || '');
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = React.useState(
    Boolean(state.displayName)
  );

  // Auto-suggest initials from first and last name
  React.useEffect(() => {
    if (!initialsManuallyEdited && state.firstName && state.lastName && !state.initials) {
      const suggested = `${state.firstName[0].toUpperCase()}${state.lastName[0].toUpperCase()}`;
      setLocalInitials(suggested);
    }
  }, [state.firstName, state.lastName, state.initials, initialsManuallyEdited]);

  // Auto-suggest display name from first and last name for creator plan
  React.useEffect(() => {
    if (isCreatorPlan && !displayNameManuallyEdited && state.firstName && state.lastName) {
      setLocalDisplayName(`${state.firstName} ${state.lastName}`);
    }
  }, [isCreatorPlan, state.firstName, state.lastName, displayNameManuallyEdited]);

  const handleContinue = async () => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    if (!state.selectedTier) {
      setLocalError('No plan selected. Please go back and select a plan.');
      return;
    }

    // Validate initials for tiers that need ALI IDs
    if (needsInitials) {
      if (!localInitials || localInitials.length !== 2) {
        setLocalError('Initials must be exactly 2 letters');
        return;
      }
      if (!/^[A-Za-z]{2}$/.test(localInitials)) {
        setLocalError('Initials must be letters only');
        return;
      }
    }

    // Validate business fields for commercial tier
    if (isCommercialPlan) {
      if (!localBusinessName.trim()) {
        setLocalError('Business name is required for commercial accounts');
        return;
      }
      if (!localBusinessType) {
        setLocalError('Business type is required for commercial accounts');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setLocalError(null);

    // Update context with initials, display name, and business info before API call
    const uppercasedInitials = localInitials.toUpperCase();
    if (needsInitials) {
      setContactInfo({ initials: uppercasedInitials });
    }
    if (isCreatorPlan) {
      setContactInfo({ displayName: localDisplayName.trim() });
    }
    if (isCommercialPlan) {
      setContactInfo({ businessName: localBusinessName.trim(), businessType: localBusinessType });
    }

    try {
      const response = await selectPlan(
        state.signupToken,
        state.selectedTier,
        needsInitials ? uppercasedInitials : undefined,
        isCommercialPlan ? localBusinessName.trim() : undefined,
        isCommercialPlan ? localBusinessType : undefined,
        needsInitials ? state.billingPeriod : undefined,
      );

      if (response.success) {
        if (response.requiresPayment) {
          if (response.stripeCheckoutUrl) {
            if (onPaymentRedirect) {
              onPaymentRedirect(response.stripeCheckoutUrl);
            } else {
              window.location.href = response.stripeCheckoutUrl;
            }
          } else {
            setLocalError('Unable to connect to payment service. Please try again later.');
          }
        } else {
          // Free tier - proceed to next step (skip payment)
          onNext();
        }
      } else {
        setLocalError(response.error || response.message || 'Failed to select plan');
      }
    } catch (error) {
      const message = (error as Error).message || 'An unexpected error occurred';
      setLocalError(message);
    } finally {
      setLoading(false);
    }
  };

  // Expose submit handler for parent component
  React.useEffect(() => {
    (window as unknown as { __signupStep4Submit: () => Promise<void> }).__signupStep4Submit =
      handleContinue;
    return () => {
      delete (window as unknown as { __signupStep4Submit?: () => Promise<void> })
        .__signupStep4Submit;
    };
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        ALI ID Prefix Selection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isDelegatePlan
          ? 'This identifier becomes part of your permanent account.'
          : 'This identifier becomes part of your permanent catalog record.'}
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {isCreatorPlan && (
        <TextField
          fullWidth
          label="Display Name"
          value={localDisplayName}
          onChange={(e) => {
            setDisplayNameManuallyEdited(true);
            setLocalDisplayName(e.target.value);
          }}
          margin="normal"
          inputProps={{ maxLength: 100 }}
          helperText={DISPLAY_NAME_HELPER_TEXT}
          sx={{ mt: 1 }}
        />
      )}

      {needsInitials && (
        <>
          <TextField
            fullWidth
            label="ALI ID Prefix (2 letters)"
            value={localInitials}
            onChange={(e) => {
              setInitialsManuallyEdited(true);
              setLocalInitials(e.target.value.toUpperCase().slice(0, 2));
            }}
            margin="normal"
            required
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            helperText={isDelegatePlan
              ? 'Choose a 2-letter prefix for your permanent ALI ID (e.g., JD-3K7). It cannot be changed later.'
              : 'Choose a 2-letter prefix for your permanent ALI ID. This prefix will appear in your catalog numbers (e.g., JD-3K7). It cannot be changed later.'}
            sx={{ mt: 1 }}
          />
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight="medium">Learn More</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" component="div">
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  <li>Your ALI ID is a unique identifier assigned to you as a cataloger.</li>
                  <li>The prefix cannot be changed once your account is created.</li>
                  <li>Format: your 2-letter prefix + a randomly generated suffix (e.g., JD-3K7).</li>
                  <li>Every item you catalog will be numbered under your ALI ID (e.g., JD-3K7.0001).</li>
                  <li>Most people use their first and last initials.</li>
                  <li>If your preferred initials are taken, you can choose any 2-letter combination.</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {isCommercialPlan && (
        <>
          <TextField
            fullWidth
            label="Business Name"
            value={localBusinessName}
            onChange={(e) => setLocalBusinessName(e.target.value)}
            margin="normal"
            required
            helperText="The name of your gallery, estate, institution, or organization"
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Business Type"
            value={localBusinessType}
            onChange={(e) => setLocalBusinessType(e.target.value)}
            margin="normal"
            required
            helperText="Select the type that best describes your organization"
          >
            {BUSINESS_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </>
      )}

      {isPaidPlan && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You will be redirected to Stripe to complete payment. Your account will be created after
          successful payment.
        </Alert>
      )}
    </Box>
  );
};

export default SignupStep4AccountDetails;
